import { z } from 'zod'

export function useFormMixin(options) {
	if (!options || !options.schema) {
		throw new Error('useFormMixin requires { schema, onSubmit?, initialValues? }')
	}

	const schema = options.schema
	const initialValues = options.initialValues || {}
	const onSubmit = options.onSubmit || (function () {})

	function getDefaultValue(fieldSchema) {
		if (fieldSchema instanceof z.ZodDefault) {
			return fieldSchema.def.defaultValue()
		}
		if (fieldSchema instanceof z.ZodOptional || fieldSchema instanceof z.ZodNullable) {
			return undefined
		}
		if (fieldSchema instanceof z.ZodString) return ''
		if (fieldSchema instanceof z.ZodNumber) return 0
		if (fieldSchema instanceof z.ZodBoolean) return false
		if (fieldSchema instanceof z.ZodArray) return []
		return undefined
	}

	const shape = schema.shape
	const defaultValues = {}
	Object.keys(shape).forEach((key) => {
		defaultValues[key] = getDefaultValue(shape[key])
	})

	function normalizePath(path) {
		return Array.isArray(path) ? path.map(String) : String(path).split('.').filter(Boolean)
	}

	function pathToString(path) {
		return normalizePath(path).join('.')
	}

	function getValueAtPath(obj, path) {
		return normalizePath(path).reduce((current, segment) => {
			if (current == null) return undefined
			return current[segment]
		}, obj)
	}

	function setValueAtPath(vm, obj, path, value) {
		const segments = normalizePath(path)
		const lastSegment = segments.pop()
		if (lastSegment == null) return

		let current = obj
		segments.forEach((segment, index) => {
			if (current[segment] == null) {
				const nextSegment = segments[index + 1]
				const container = /^\d+$/.test(nextSegment) ? [] : {}
				vm.$set(current, segment, container)
			}
			current = current[segment]
		})

		vm.$set(current, lastSegment, value)
	}

	function getSchemaAtPath(rootSchema, path) {
		return normalizePath(path).reduce((currentSchema, segment) => {
			if (!currentSchema) return undefined
			if (currentSchema instanceof z.ZodDefault) currentSchema = currentSchema.def.innerType
			if (currentSchema instanceof z.ZodOptional || currentSchema instanceof z.ZodNullable) {
				currentSchema = currentSchema.unwrap()
			}
			if (currentSchema instanceof z.ZodObject) {
				return currentSchema.shape[segment]
			}
			if (currentSchema instanceof z.ZodArray) {
				return currentSchema.element
			}
			return undefined
		}, rootSchema)
	}

	const mergedValues = Object.assign({}, defaultValues, initialValues)

	const initialErrors = {}
	const initialFieldStates = {}
	Object.keys(shape).forEach((key) => {
		initialErrors[key] = undefined
		initialFieldStates[key] = { touched: false, dirty: false }
	})

	return {
		data() {
			return {
				form: {
					schema,
					_defaultValues: defaultValues,
					_initialValues: initialValues,
					_onSubmit: onSubmit,

					values: Object.assign({}, mergedValues),
					errors: Object.assign({}, initialErrors),
					fieldStates: Object.assign({}, initialFieldStates),
					isSubmitting: false,
				},
			}
		},

		computed: {
			formFields() {
				const fieldBindings = {}
				const shapeKeys = Object.keys(this.form.schema.shape)

				shapeKeys.forEach((key) => {
					fieldBindings[key] = {
						value: this.form.values[key],
						input: (value) => this.formHandleInput(key, value),
						blur: () => this.formHandleBlur(key),
						errorMessages: this.form.errors[key] ? [this.form.errors[key]] : [],
					}
				})

				return fieldBindings
			},

			formHasErrors() {
				return Object.keys(this.form.errors).some((k) => this.form.errors[k] !== undefined)
			},
		},

		methods: {
			formField(fieldName) {
				const path = pathToString(fieldName)
				return {
					value: getValueAtPath(this.form.values, path),
					input: (value) => this.formHandleInput(path, value),
					blur: () => this.formHandleBlur(path),
					errorMessages: this.form.errors[path] ? [this.form.errors[path]] : [],
				}
			},

			formValidateField(fieldName) {
				const path = pathToString(fieldName)
				try {
					const fieldSchema = getSchemaAtPath(this.form.schema, path)
					if (!fieldSchema) throw new Error(`Unknown form field: ${path}`)
					fieldSchema.parse(getValueAtPath(this.form.values, path))
					this.$set(this.form.errors, path, undefined)
					return true
				} catch (error) {
					if (error instanceof z.ZodError) {
						const msg = (error.issues && error.issues[0] && error.issues[0].message) || 'Invalid value'
						this.$set(this.form.errors, path, msg)
					}
					return false
				}
			},

			formValidateAll() {
				try {
					this.form.schema.parse(this.form.values)
					Object.keys(this.form.errors).forEach((key) => {
						this.$set(this.form.errors, key, undefined)
					})
					return true
				} catch (error) {
					if (error instanceof z.ZodError) {
						Object.keys(this.form.errors).forEach((key) => {
							this.$set(this.form.errors, key, undefined)
						})
						error.issues.forEach((err) => {
							const fieldName = err.path && err.path.length ? pathToString(err.path) : undefined
							if (fieldName != null) {
								this.$set(this.form.errors, fieldName, err.message)
							}
						})
					}
					return false
				}
			},

			formHandleBlur(fieldName) {
				const path = pathToString(fieldName)
				if (!this.form.fieldStates[path]) {
					this.$set(this.form.fieldStates, path, { touched: false, dirty: false })
				}

				this.form.fieldStates[path].touched = true

				if (this.form.fieldStates[path].dirty) {
					this.formValidateField(path)
				}
			},

			formHandleInput(fieldName, value) {
				const path = pathToString(fieldName)
				setValueAtPath(this, this.form.values, path, value)

				if (!this.form.fieldStates[path]) {
					this.$set(this.form.fieldStates, path, { touched: false, dirty: false })
				}

				this.form.fieldStates[path].dirty = true

				if (this.form.errors[path]) {
					this.formValidateField(path)
				}
			},

			formAddError(field, message) {
				this.$set(this.form.errors, field, message)
			},

			async formHandleSubmit() {
				Object.keys(this.form.fieldStates).forEach((key) => {
					this.form.fieldStates[key].touched = true
					this.form.fieldStates[key].dirty = true
				})

				const isValid = this.formValidateAll()
				if (!isValid) return

				this.form.isSubmitting = true

				try {
					Object.keys(this.form.errors).forEach((key) => {
						this.$set(this.form.errors, key, undefined)
					})

					await this.form._onSubmit.call(this, this.form.values, this.formAddError)
				} finally {
					this.form.isSubmitting = false
				}
			},

			formReset() {
				const resetValues = Object.assign({}, this.form._defaultValues, this.form._initialValues)
				Object.keys(resetValues).forEach((key) => {
					this.$set(this.form.values, key, resetValues[key])
				})

				Object.keys(this.form.errors).forEach((key) => {
					this.$set(this.form.errors, key, undefined)
				})

				Object.keys(this.form.fieldStates).forEach((key) => {
					this.$set(this.form.fieldStates, key, { touched: false, dirty: false })
				})
			},
		},
	}
}
